// Auth actions + the onAuthStateChanged listener that resolves the profile.
import { useCallback, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { authApi } from '../api/auth';
import { ApiError } from '../api/functionsClient';
import { useAuthStore } from '../store/authStore';
import { useWalletStore } from '../store/walletStore';

// Wire the Firebase auth listener to our stores. Call once, high in the tree.
export function useAuthListener(): void {
  const setFirebaseUser = useAuthStore((s) => s.setFirebaseUser);
  const setProfile = useAuthStore((s) => s.setProfile);
  const setLoading = useAuthStore((s) => s.setLoading);
  const setBalance = useWalletStore((s) => s.setBalance);
  const reset = useAuthStore((s) => s.reset);

  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);
      if (!user) {
        reset();
        setLoading(false);
        return;
      }
      try {
        // /auth/me self-heals a missing profile (CLAUDE.md §1.1).
        const me = await authApi.me();
        setProfile(me.user, me.role);
        setBalance(me.wallet.balance);
      } catch (err) {
        // If provisioning never ran, try once before giving up.
        if (err instanceof ApiError && err.status >= 400) {
          try {
            await authApi.postRegister();
            const me = await authApi.me();
            setProfile(me.user, me.role);
            setBalance(me.wallet.balance);
          } catch {
            setProfile(null, null);
          }
        }
      } finally {
        setLoading(false);
      }
    });
  }, [setFirebaseUser, setProfile, setLoading, setBalance, reset]);
}

export function useAuth() {
  const register = useCallback(
    async (email: string, password: string, displayName: string) => {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) await updateProfile(cred.user, { displayName });
      // Immediately provision Firestore docs + role claim (§1.1). Retry on fail.
      try {
        await authApi.postRegister();
      } catch {
        await authApi.postRegister().catch(() => undefined);
      }
      await sendEmailVerification(cred.user).catch(() => undefined);
      return cred.user;
    },
    [],
  );

  const login = useCallback(async (email: string, password: string) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred.user;
  }, []);

  const logout = useCallback(() => signOut(auth), []);

  const resendVerification = useCallback(async () => {
    if (auth.currentUser) await sendEmailVerification(auth.currentUser);
  }, []);

  const forgotPassword = useCallback(
    (email: string) => sendPasswordResetEmail(auth, email),
    [],
  );

  return { register, login, logout, resendVerification, forgotPassword };
}
